import { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import FlagRounded from '@mui/icons-material/FlagRounded';
import { ROUTES } from 'constants/RoutePaths';
import { userToken } from '_actions/user_actions';
import TSInfoAlert from 'components/common/InfoAlert/InfoAlert';
import Notice from 'components/common/Notice/Notice';
import ContactChoice from 'components/TrailSafe/ContactChoice/ContactChoice';
import MLModal from 'components/common/Modal/Modal';
import EmergencyContact from 'pages/Protected/User/EmergencyContact/EmergencyContact';
import MLToast from 'components/common/Toast/Toast';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetUserDetailsQuery } from 'app/api/usersApiSlice';
import {
  useCreateActivityMutation,
  useCreateCheckinMutation,
} from 'app/api/activityApiSlice';
import TSButton from 'components/common/Button/Button';
import TSCounter from 'components/common/Counter/Counter';
import LogSuccessMsg from './LogSuccessMessage';
import GearChecklist from 'components/TrailSafe/GearList/GearChecklist';
import { useGetTrailGearQuery } from 'app/api/trailApiSlice';
import MLDialog from 'components/common/Dialog/Dialog';

const LogActivity = ({
  option,
  toggleModal,
  checkinPointDetails = null,
  isCheckin = false,
}) => {
  const userEmail = useSelector((state) => state.global?.userEmail);
  const navigate = useNavigate();
  const [value, setValue] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [additionalWalkers, setAdditionalWalkers] = useState(0);
  const [isAuth, setIsAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [emCont, setEmCont] = useState([]);
  const [currActivity, setCurrActivity] = useState(null);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  // The walker's Gear List answers, sent with the start: status by suggested
  // item key, and the names of their own items. The list is fixed once the
  // Activity starts (ADR-0017).
  const [gearAnswers, setGearAnswers] = useState({});
  const [ownGearItems, setOwnGearItems] = useState([]);

  const QRCodeId = useSelector((state) => state.global.qrCodeId);

  const { showToast } = MLToast();

  const trailId = checkinPointDetails?.trailid ?? option?.trailid;
  const {
    data: trailGear,
    isLoading: isTrailGearLoading,
    isError: isTrailGearError,
  } = useGetTrailGearQuery(trailId, {
    skip: !trailId,
  });

  const { data: userData, isLoading: isUserLoading } = useGetUserDetailsQuery(
    userEmail,
    { skip: !userEmail }
  );

  useEffect(() => {
    const checkAuth = async () => {
      const t = await userToken();
      setIsAuth(!!t);
    };
    checkAuth();
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (userData) {
      if (userData?.[0]?.emergency_contacts) {
        setEmCont([...userData?.[0]?.emergency_contacts]);
      }
      if (userData?.[0]?.active_checkin) {
        setCurrActivity([...userData?.[0]?.active_checkin]);
      }
    }
  }, [userData]);

  const [
    createActivity,
    {
      data: createActivityData,
      isLoading: isCreateActivityLoading,
      isError: isCreateActivityError,
      isSuccess: isCreateActivitySuccess,
    },
  ] = useCreateActivityMutation();

  const [
    createCheckin,
    {
      data: createCheckinData,
      isLoading: isCreateCheckinLoading,
      isError: isCreateCheckinError,
      isSuccess: isCreateCheckinSuccess,
    },
  ] = useCreateCheckinMutation();

  const toggleContactModal = (state) => {
    setIsModalOpen(state);
  };

  const LogComplete = () => {
    toggleModal(false);
    setShowSuccessMsg(false);
    navigate(`/${ROUTES.HOME}`);
  };

  // The walker and the start/check-in times come from the server. qr_code is
  // the scanned sign's permanent code, if the walker came from one.
  const handleLogActivity = async () => {
    const res = await createActivity({
      trail_option_id: option?.TrailOptionID ?? option.trailoptionid,
      emergency_contact_id: value,
      participants: additionalWalkers,
      qr_code: QRCodeId,
      gear_answers: gearAnswers,
      own_gear_items: ownGearItems,
    });
    if (res.data?.[0]?.is_success) {
      //nav to activity page or show success message
      showToast('Walk started', 'success');
      setShowSuccessMsg(true);
    } else {
      showToast(res.error?.data?.message || 'Something went wrong!', 'error');
    }
  };

  const handleLogCheckin = async () => {
    const res = await createCheckin({
      activity_id: currActivity?.[0]?.ActivityID,
      trail_option_id: option?.TrailOptionID ?? option.trailoptionid,
      qr_code: QRCodeId,
    });
    if (res.data?.[0]?.is_success) {
      toggleModal(false);
      //nav to activity page or show success message
      showToast('Check-in saved', 'success');
      navigate(`/${ROUTES.HOME}`);
    } else {
      showToast(res.error?.data?.message || 'Something went wrong!', 'error');
    }
  };

  if (isUserLoading || isLoading) {
    return <div>Loading...</div>;
  } else {
    const defaultContact = emCont.find((contact) => contact.IsPrimary);
    if (defaultContact && !value) {
      setValue(defaultContact.EmergencyContactID);
    }
    return (
      <>
        {isCheckin && currActivity ? (
          <div>
            <Typography variant="h2">Log Check-in</Typography>
            <Typography variant="body1">
              You're at {checkinPointDetails?.checkinpointlocation}, heading to{' '}
              {option ? option.TrailOptionName : 'No option selected'}
            </Typography>
            {currActivity[0].ParkID !== checkinPointDetails?.parkid && (
              <TSInfoAlert
                info1={
                  'Checkin location does not match the location of your current activity!'
                }
                info2={`${checkinPointDetails?.checkinpointlocation} is in ${checkinPointDetails?.parkname}, but you have a current activity in ${currActivity[0].ParkName}. If you have already left the previous park, please end the previous activity and start a new one for better tracking.`}
                alertType={`error-alert`}
                alertIcon={'Error-tick'}
              />
            )}
            {currActivity[0].ParkID === checkinPointDetails?.parkid &&
              currActivity[0].TrailID !== checkinPointDetails?.trailid && (
                <TSInfoAlert
                  info1={
                    'Checkin trail does not match the trail of your current activity!'
                  }
                  info2={
                    'The check-in will still be logged but please be cautious and make sure you are on the right trail.'
                  }
                  alertType={`warning-alert`}
                  alertIcon={'Warning'}
                />
              )}
            <div className="flex gap-4 mt-4">
              <TSButton
                btnText="Confirm Check-in"
                classList="py-4"
                onClickFunc={() => handleLogCheckin()}
              />
              <TSButton
                btnText="Cancel"
                btnVariant="outlined"
                classList="py-4"
                onClickFunc={() => toggleModal(false)}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <Typography variant="h2">Start a walk</Typography>
              {option?.trailname && (
                <Typography variant="body1" color="text.secondary">
                  {option.trailname}
                  {option.parkname && `, ${option.parkname}`}
                </Typography>
              )}
            </div>

            <section className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Typography variant="h4" component="h3">
                  Emergency Contact
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Who should we contact if you don't come back on time?
                </Typography>
              </div>
              <ContactChoice
                contacts={emCont}
                value={value}
                onChange={setValue}
                onAdd={() => toggleContactModal(true)}
              />
            </section>

            <section className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Typography variant="h4" component="h3">
                  Other walkers with you
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Count people walking with you who are not starting a walk in
                  TrailSafe themselves. Don't count yourself.
                </Typography>
              </div>
              <div>
                <TSCounter
                  count={additionalWalkers}
                  setCount={setAdditionalWalkers}
                  labels={['One less walker', 'One more walker']}
                />
              </div>
            </section>

            {trailGear?.gear_list && (
              <GearChecklist
                items={[
                  ...trailGear.gear_list.map((item) => ({
                    ...item,
                    id: item.key,
                    status: gearAnswers[item.key] ?? 'UNANSWERED',
                  })),
                  ...ownGearItems.map((name, index) => ({
                    id: `own-${index}`,
                    name,
                    essential: false,
                    status: 'TAKING',
                    walker_added: true,
                  })),
                ]}
                onMark={(item, status) =>
                  setGearAnswers((answers) => ({
                    ...answers,
                    [item.id]: status,
                  }))
                }
                onMarkEssentials={() =>
                  setGearAnswers((answers) => ({
                    ...answers,
                    ...Object.fromEntries(
                      trailGear.gear_list
                        .filter((item) => item.essential)
                        .map((item) => [item.key, 'TAKING'])
                    ),
                  }))
                }
                onAdd={(name) => {
                  setOwnGearItems((items) => [...items, name]);
                  return true;
                }}
                onRemove={(item) =>
                  setOwnGearItems((items) =>
                    items.filter((_, index) => `own-${index}` !== item.id)
                  )
                }
              />
            )}
            {isTrailGearError && (
              <Notice tone="info" title="We couldn't load the Gear List">
                You can still start, but check you have what you need.
              </Notice>
            )}
            <div className="flex flex-col gap-2">
              <TSButton
                btnText={
                  isCreateActivityLoading
                    ? 'Starting...'
                    : isTrailGearLoading
                      ? 'Loading Gear List...'
                      : !value
                        ? 'Choose a contact to start'
                        : 'Start walk'
                }
                icon={<FlagRounded />}
                // The Gear List is fixed once started, so the walker sees it
                // before they can start.
                btnDisable={
                  !value || isCreateActivityLoading || isTrailGearLoading
                }
                onClickFunc={() => handleLogActivity()}
              />
              <TSButton
                btnText="Cancel"
                btnVariant="text"
                onClickFunc={() => toggleModal(false)}
              />
            </div>
          </div>
        )}
        <MLModal open={isModalOpen} setOpenModal={setIsModalOpen}>
          <EmergencyContact
            contact={null}
            index={99}
            isModal={true}
            toggleModal={setIsModalOpen}
          />
        </MLModal>
        <MLDialog openDialog={showSuccessMsg} setOpenDialog={setShowSuccessMsg}>
          <LogSuccessMsg
            handleDismiss={() => LogComplete()}
            safetySheetUrl={option?.SafetySheetURL ?? option?.safetysheeturl}
          />
        </MLDialog>
      </>
    );
  }
};

export default LogActivity;
